"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP);
export default function Consultation() {
  const container = useRef<HTMLDivElement>(null);
  const [time, setTime] = useState<string>("");
  const prefersReducedMotion = useReducedMotion();
  useEffect(() => {
    const update = () => {
      try {
        const now = new Date();
        const formatted = new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: "Europe/London"
        }).format(now);
        setTime(formatted);
      } catch {
        const d = new Date();
        setTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
      }
    };
    update();
    const id = window.setInterval(update, 60000);
    return () => window.clearInterval(id);
  }, []);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".narr-line-inner", {
        yPercent: 110
      });
      gsap.set(".narr-kicker", {
        y: 12
      });
      gsap.set(".narr-body", {
        y: 12
      });
      gsap.set(".narr-cta", {
        y: 12
      });
      gsap.set(".narr-clock", {
        y: 12
      });
      gsap.set(".narr-stroke-path", {
        strokeDasharray: 340,
        strokeDashoffset: 340
      });
      const tl = gsap.timeline();
      tl.to(".narr-kicker", {
        opacity: 1,
        y: 0,
        duration: 0.52,
        ease: "power3.out"
      }).to(".narr-line-inner", {
        yPercent: 0,
        duration: 0.78,
        stagger: 0.09,
        ease: "cubic-bezier(0.22, 1, 0.36, 1)" as unknown as string
      }, "-=0.2").to(".narr-line", {
        opacity: 1,
        duration: 0.4,
        stagger: 0.09
      }, "<").to(".narr-stroke-path", {
        strokeDashoffset: 0,
        duration: 0.62,
        ease: "power2.inOut"
      }, "-=0.15").to(".narr-body", {
        opacity: 1,
        y: 0,
        duration: 0.52,
        ease: "power3.out"
      }, "-=0.25").to(".narr-cta", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power3.out"
      }, "-=0.3").to(".narr-clock", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        ease: "power3.out"
      }, "<");
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".narr-kicker", ".narr-line", ".narr-body", ".narr-cta", ".narr-clock"], {
        opacity: 1,
        y: 0
      });
      gsap.set(".narr-line-inner", {
        yPercent: 0
      });
      gsap.set(".narr-stroke-path", {
        strokeDashoffset: 0
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="30625" id="consultationcta" data-nav="light" ref={container} className="relative w-full bg-background text-foreground border-t border-b border-border py-[clamp(120px,18vh,240px)]">
      <div className="mx-auto w-full max-w-[1440px] px-[clamp(20px,4vw,80px)]">
        <div className="grid grid-cols-12 gap-x-6">
          <div className="col-span-12">
            <p className="narr-kicker font-sans text-[clamp(13px,0.92vw,15px)] font-medium uppercase tracking-[0.16em] text-muted-foreground" style={{
            opacity: 0
          }}>
              {"Work with me"}
            </p>
          </div>

          <div className="col-span-12 mt-8 md:col-span-11">
            <h2 className="font-serif font-normal text-foreground text-2xl sm:text-3xl lg:text-4xl" style={{
            letterSpacing: "-0.03em",
            lineHeight: 0.94
          }}>
              <span className="narr-line block overflow-hidden" style={{
              opacity: 0
            }}>
                <span className="narr-line-inner block">
                  {"Let's grow"}
                </span>
              </span>
              <span className="narr-line block overflow-hidden" style={{
              opacity: 0
            }}>
                <span className="narr-line-inner block">
                  <span>{"your "}</span>
                  <span className="relative inline-block italic">
                    <span className="relative z-10">{"visibility"}</span>
                    <svg aria-hidden="true" className="pointer-events-none absolute left-0 right-0 z-0" style={{
                    bottom: "0.08em",
                    width: "100%",
                    height: "0.18em",
                    transform: "rotate(-2.5deg)",
                    transformOrigin: "left center"
                  }} viewBox="0 0 340 20" preserveAspectRatio="none" fill="none">
                      <path className="narr-stroke-path" d="M4 12 C 60 6, 130 16, 200 9 S 320 7, 336 11" stroke="var(--primary)" strokeWidth="9" strokeLinecap="round" strokeOpacity="0.92" fill="none" />
                    </svg>
                  </span>
                  <span style={{
                  color: "var(--primary)"
                }}>{"."}</span>
                </span>
              </span>
            </h2>
          </div>

          <div className="col-span-12 mt-12 grid grid-cols-12 gap-x-6 gap-y-10">
            <div className="col-span-12 md:col-span-8">
              <p className="narr-body font-sans text-[clamp(20px,1.55vw,25px)] font-normal leading-[1.55] text-foreground/80" style={{
              opacity: 0,
              letterSpacing: "-0.005em"
            }}>
                {"Send me a message about your SEO goals. I reply personally within two working days — usually with a few questions to help me understand your needs."}
              </p>

              <div className="narr-cta mt-10 flex flex-wrap items-center gap-x-8 gap-y-5" style={{
              opacity: 0
            }}>
                <motion.div whileHover={prefersReducedMotion ? undefined : "hover"} initial="rest" animate="rest">
                  <Button asChild className="group h-14 rounded-lg bg-primary px-8 text-[17px] font-medium tracking-[0.02em] text-primary-foreground transition-colors duration-300 hover:bg-primary/90">
                    <Link to="/ContactPage">
                      <motion.span className="inline-flex items-center" variants={{
                      rest: {
                        x: 0
                      },
                      hover: {
                        x: 4
                      }
                    }} transition={{
                      duration: 0.26,
                      ease: "easeOut"
                    }}>
                        {"Book a consultation"}
                      </motion.span>
                      <motion.span className="inline-flex items-center overflow-hidden" variants={{
                      rest: {
                        width: 0,
                        opacity: 0,
                        marginLeft: 0
                      },
                      hover: {
                        width: 20,
                        opacity: 1,
                        marginLeft: 8
                      }
                    }} transition={{
                      duration: 0.26,
                      ease: "easeOut"
                    }}>
                        <ArrowRight className="size-5" strokeWidth={1.5} />
                      </motion.span>
                    </Link>
                  </Button>
                </motion.div>

                <Link to="mailto:contact@ye-consultancy.com" className="group relative font-sans text-[17px] font-medium tracking-[0.02em] text-foreground">
                  <span className="relative inline-block pb-1.5">
                    {"contact@ye-consultancy.com"}
                    <span aria-hidden="true" className="absolute left-0 right-0 bottom-0 h-px bg-foreground transition-opacity duration-200 group-hover:opacity-0" />
                    <svg aria-hidden="true" className="pointer-events-none absolute left-0 -bottom-0.5 w-full opacity-0 transition-opacity duration-200 group-hover:opacity-100" style={{
                    height: "8px"
                  }} viewBox="0 0 200 8" preserveAspectRatio="none" fill="none">
                      <path d="M2 4.5 C 40 2, 90 6.5, 130 3.5 S 190 3, 198 5" stroke="var(--primary)" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.92" fill="none" />
                    </svg>
                  </span>
                </Link>
              </div>
            </div>

            <div className="col-span-12 md:col-span-4 md:flex md:items-end md:justify-end">
              <div className="narr-clock flex flex-col md:items-end" style={{
              opacity: 0
            }}>
                <span className="font-sans text-[clamp(13px,0.92vw,15px)] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                  {"UK · GMT"}
                </span>
                <span className="mt-2 font-sans text-[clamp(23px,1.84vw,30px)] font-medium tabular-nums tracking-[0.02em] text-foreground">
                  {time || "--:--"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}